import { AnyAction, createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import {
  disableManualSave,
  enableManualSave,
  updateSheetManagerState,
  updateSheetView,
  updateSheetsStateWithPreCalculatedHistory,
} from './sheetsManagerSlice';
import { RootState } from '../../core/redux/store';
import sessionStorageSingleton from '../../core/configs/sessionStorageSingleton';
import {
  UserKeyStore,
  UserStorageDataType,
  UserStorageFlags,
} from '../../core/database/schemas';
import { UserStorageParams } from '../../core/database/user/types';
import userStorageWorker from '../../core/database/user/userWorkerManager';
import { saveSheetActiveCell } from '../databaseDeprecated/sheets/ls/ls';
import { tabSharedWorker } from '../../core/sharedWorker/sharedWorkerManager';
import { SharedWorkerMessage } from '../../core/sharedWorker/types';
import { TabSyncedChanges } from '../types';
import { saveManualModeToggle } from '../../core/utils/auth/authUtils';

const sheetsManagerListenerMiddleware = createListenerMiddleware();

sheetsManagerListenerMiddleware.startListening({
  matcher: isAnyOf(
    updateSheetManagerState,
    updateSheetsStateWithPreCalculatedHistory,

    // manualModeChangesSaved,
    // manualModeChangesDiscarded,
    enableManualSave,
    disableManualSave,
    updateSheetView
  ),
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    const userId = state.auth.user.id;
    const feKey = await sessionStorageSingleton.getFeKey(
      UserKeyStore.UserFeKey
    );

    const isManualSave = state.sheetsManager.isManualSave;

    if (state.auth.user.id && state.auth.app.isMasterTab && feKey) {
      if (
        isAnyOf(
          updateSheetManagerState,
          updateSheetsStateWithPreCalculatedHistory
        )(action)
      ) {
        await saveUserSheetHistory(userId, feKey, action, isManualSave);
      } else if (isAnyOf(enableManualSave, disableManualSave)(action)) {
        await saveManualSaveFlag(userId, feKey, action, isManualSave);
      } else if (updateSheetView.toString() === action.type) {
        const activeCell = action.payload.activeCell;
        const sheetId = action.payload.sheetId;
        if (activeCell && sheetId) {
          saveSheetActiveCell(sheetId, activeCell);
        }
      }
    }

    if (
      isAnyOf(
        updateSheetManagerState,
        updateSheetsStateWithPreCalculatedHistory
      )(action)
    ) {
      await syncDataWithTabs(userId, action);
    }
  },
});

const saveManualSaveFlag = async (
  userId: string,
  feKey: CryptoKey,
  action: AnyAction,
  isManualSave: boolean
) => {
  // console.log("listening to manual save flag....", isManualSave)
  saveManualModeToggle(isManualSave ? userId : undefined);
  // const params: UserStorageParams = {
  //   data: {
  //     dataType: UserStorageDataType.Flags,
  //     flagKey: UserStorageFlags.ManualSaveMode,
  //     flagValue: isManualSave,
  //   },
  //   userId,
  //   feKey,
  //   actionType: action.type,
  // };
  // userStorageWorker.postMessage(params);
};

const saveUserSheetHistory = async (
  userId: string,
  userFeKey: CryptoKey,
  action: AnyAction,
  isManualSave: boolean
) => {
  const past = action.payload.past;
  if (!isManualSave) {
    let dataType = UserStorageDataType.History;
    if (action.type === updateSheetsStateWithPreCalculatedHistory.toString()) {
      const payload = action.payload;
      dataType = payload.historyAction as
        | UserStorageDataType.Undo
        | UserStorageDataType.Redo;
    }
    const params: UserStorageParams = {
      actionType: action.type,
      userId: userId,
      feKey: userFeKey,
      data: {
        dataType,
        past,
      },
    };
    userStorageWorker.postMessage(params);
  }
};

const syncDataWithTabs = async (
  userId: string,
  action:
    | ReturnType<typeof updateSheetManagerState>
    | ReturnType<typeof updateSheetsStateWithPreCalculatedHistory>
) => {
  let historyAction = UserStorageDataType.History;
  if (isAnyOf(updateSheetsStateWithPreCalculatedHistory)(action)) {
    historyAction = action.payload.historyAction;
  }
  const params: TabSyncedChanges = {
    historyAction,
    userId,
    updatedSheets: action.payload.updatedSheets,
    updatedGroups: action.payload.updatedGroups,
    updatedSheetsIdToName: action.payload.updatedSheetsIdToName,
    present: action.payload.present,
    past: action.payload.past,
  };
  tabSharedWorker.port.postMessage({
    type: SharedWorkerMessage.BroadcastChangesToTabs,
    userId,
    params: params,
  });
};

export default sheetsManagerListenerMiddleware.middleware;
